import { todayWithSystemTimeZone, convertTimeZone } from '@magicdoorinc/date';
import { toast, useLocalization, createLazyResource, createMutation } from '@magicdoorinc/ui';
import { useNavigate } from '@solidjs/router';
import { createMemo } from 'solid-js';
import { hasActivePermission } from '~/config/auth';
import { useBankAccountsList } from '~/contexts/bank-accounts';
import { useCurrentCompany } from '~/contexts/companies';
import { checkNeedsBankAccountSetup } from '~/contexts/onboarding/checkNeedsBankAccountSetup';
import { createMagicDoorContext } from '~/contexts/utils';
import { mode } from '~/hooks/usePaymentMode';
import { companyRepository } from '~/repositories/companiesRepository';
import { onboardingExperienceRepository } from '~/repositories/onboardingExperienceRepository';
import { onboardingRepository } from '~/repositories/onboardingRepository';
import { phonesRepository } from '~/repositories/phones';
import { AiLeaseRenewalSettingsRepository } from '~/repositories/settings/aiLeaseRenewalSettingsRepository';
import { chatsSettingsRepository } from '~/repositories/settings/chatsSettingsRepository';
import { LeaseRentersInsuranceSettingsRepository } from '~/repositories/settings/leaseRentersInsuranceSettingsRepository';
import { rentPaymentNotificationsSettingsRepository } from '~/repositories/settings/rentPaymentNotificationsSettingsRepository';
import { tenantAutoPayFailedNotificationRepository } from '~/repositories/settings/tenantAutoPayFailedNotificationRepository';
import { tenantPaymentReminderRepository } from '~/repositories/settings/tenantPaymentReminderRepository';
import { OnboardingStatus } from '~/utils/constant';
import type {
  ChatAILeaseRenewalSettingsCompanyPortalTypedSettingsWithOverridesDocument,
  ChatAILeaseSettingsCompanyPortalTypedSettingsWithOverridesDocument,
  ChatAIMaintenanceRequestsSettingsCompanyPortalTypedSettingsWithOverridesDocument,
  LeaseRentersInsuranceSettingsCompanyPortalTypedSettingsWithOverridesDocument,
  RentPaymentNotificationSettingsCompanyPortalTypedSettingsWithOverridesDocument,
  TenantAutoPayFailedNotificationSettingsCompanyPortalTypedSettingsWithOverridesDocument,
  TenantPaymentReminderSettingsCompanyPortalTypedSettingsWithOverridesDocument,
  TextSettingsCompanyPortalTypedSettingsWithOverridesDocument,
} from '~/swagger/settings/Api';

export const [OnboardingProvider, useOnboarding] = createMagicDoorContext('Onboarding', () => {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const bankAccountsList = useBankAccountsList();

  const { currentCompany, mutateCompanies } = useCurrentCompany();

  const [companyCanBeCharged] = createLazyResource(async () => {
    return await companyRepository.getCompanyCanBeCharged();
  });

  const [onboardingExperience] = createLazyResource(async () => {
    if (!hasActivePermission('onboarding_experience', 'read')) return null;
    try {
      return await onboardingExperienceRepository.getExperience();
    } catch {
      return null;
    }
  });

  const isExperienceOnboardingActive = createMemo(() => onboardingExperience()?.currentlyOnboarding === true);

  const isOnboardingCompleted = createMemo(() => {
    return currentCompany()?.onboarded;
  });

  const shouldHideOnboardingMenu = createMemo(() => {
    const company = currentCompany();
    if (!company) return true;
    if (company.onboardingStatus === OnboardingStatus.USER_HIDE_ONBOARDING) return true;
    const companyAgeInMonths = todayWithSystemTimeZone().diff(convertTimeZone(company.created), 'month', true);
    return companyAgeInMonths > 3;
  });

  const updateOnboardingStep = (status: string) => {
    mutateCompanies((prev) => (prev ? { ...prev, onboardingStatus: status } : prev));
    onboardingRepository.updateOnboardingStep(status);
  };

  const completeOnboarding = createMutation(async () => {
    await onboardingRepository.completeOnboarding();
    await onboardingRepository.updateOnboardingStep(OnboardingStatus.PAID);
    mutateCompanies((prev) => (prev ? { ...prev, onboarded: true, onboardingStatus: OnboardingStatus.PAID } : prev));
  });

  const hideOnboarding = createMutation(async () => {
    await onboardingRepository.updateOnboardingStep(OnboardingStatus.USER_HIDE_ONBOARDING);
    mutateCompanies((prev) => (prev ? { ...prev, onboardingStatus: OnboardingStatus.USER_HIDE_ONBOARDING } : prev));
    navigate('/');
  });

  const needsBankAccountSetup = async (): Promise<boolean> => {
    try {
      return await checkNeedsBankAccountSetup({
        getAccounts: bankAccountsList.bankAccounts,
        refetchAccounts: async () => await bankAccountsList.refetchBankAccounts(),
        paymentMode: mode(),
      });
    } catch (error) {
      console.error('Failed to check bank account status:', error);
      return true;
    }
  };

  const purchasePhoneNumber = createMutation(async (phoneNumber: string) => {
    await phonesRepository.updateCompanyPhoneNumber(phoneNumber);
    toast.success(t('Phone number updated successfully'));
  });

  const checkPhoneNumberStatus = async (): Promise<{
    hasPhoneNumber: boolean;
    phoneNumber?: string;
  }> => {
    const phoneStatus = await phonesRepository.canCompanySendTextMessages();
    return {
      hasPhoneNumber: !!phoneStatus?.chatPhoneNumber,
      phoneNumber: phoneStatus?.chatPhoneNumber,
    };
  };

  const getTextMessageAlertSettings = async () => {
    try {
      const [paymentReminder, paymentConfirmation, rentersInsurance, autoPayFailed, textSettings]: [
        TenantPaymentReminderSettingsCompanyPortalTypedSettingsWithOverridesDocument,
        RentPaymentNotificationSettingsCompanyPortalTypedSettingsWithOverridesDocument,
        LeaseRentersInsuranceSettingsCompanyPortalTypedSettingsWithOverridesDocument,
        TenantAutoPayFailedNotificationSettingsCompanyPortalTypedSettingsWithOverridesDocument,
        TextSettingsCompanyPortalTypedSettingsWithOverridesDocument,
      ] = await Promise.all([
        new tenantPaymentReminderRepository().getTenantPaymentReminderAll(),
        new rentPaymentNotificationsSettingsRepository().getAllRentPaymentNotificationsSettings(),
        new LeaseRentersInsuranceSettingsRepository().getAllLeaseRentersInsuranceSettings(),
        new tenantAutoPayFailedNotificationRepository().getTenantAutoPayFailedNotificationAll(),
        new chatsSettingsRepository().getAllTextSettings(),
      ]);

      return {
        paymentReminder: paymentReminder?.settings,
        paymentConfirmation: paymentConfirmation?.settings,
        rentersInsurance: rentersInsurance?.settings,
        autoPayFailed: autoPayFailed?.settings,
        textSettings: textSettings?.settings,
      };
    } catch (error) {
      console.error('Failed to get text message alert settings:', error);
      return null;
    }
  };

  const saveTextMessageAlertSettings = createMutation(
    async (settings: {
      balanceDueTextEnabled: boolean;
      paymentConfirmationTextEnabled: boolean;
      rentersInsuranceEnabled: boolean;
      autoPayFailedTextEnabled: boolean;
      includeAiTag: boolean;
    }) => {
      const existingSettings = await getTextMessageAlertSettings();

      if (!existingSettings) {
        throw new Error('Failed to get existing settings from backend');
      }

      await Promise.all([
        existingSettings.paymentReminder &&
          new tenantPaymentReminderRepository().updateTenantPaymentReminder({
            ...existingSettings.paymentReminder,
            sendPostedBalanceNotificationsAsText: settings.balanceDueTextEnabled,
            sendPaymentDueNotificationsAsText: settings.balanceDueTextEnabled,
            sendPaymentOutstandingNotificationsAsText: settings.balanceDueTextEnabled,
            sendPaymentPastDueNotificationsAsText: settings.balanceDueTextEnabled,
            sendPaymentDelinquencyNotificationsAsText: settings.balanceDueTextEnabled,
          }),
        existingSettings.paymentConfirmation &&
          new rentPaymentNotificationsSettingsRepository().changeRentPaymentNotificationsSettings({
            ...existingSettings.paymentConfirmation,
            sendRentReceivedNotificationAsText: settings.paymentConfirmationTextEnabled,
          }),
        existingSettings.rentersInsurance &&
          new LeaseRentersInsuranceSettingsRepository().updateLeaseRentersInsuranceSettings({
            ...existingSettings.rentersInsurance,
            reminderEnabled: settings.rentersInsuranceEnabled,
          }),
        existingSettings.autoPayFailed &&
          new tenantAutoPayFailedNotificationRepository().updateTenantAutoPayFailedNotification({
            ...existingSettings.autoPayFailed,
            sendNotifyOnTenantAutoPayFailedViaText: settings.autoPayFailedTextEnabled,
          }),
        existingSettings.textSettings &&
          new chatsSettingsRepository().changeTextSettings({
            ...existingSettings.textSettings,
            includeAiTag: settings.includeAiTag,
            aiTag: existingSettings.textSettings.aiTag,
          }),
      ]);

      toast.success(t('Text message alert settings saved successfully'));
    }
  );

  const getAICommunicationsSettings = async () => {
    try {
      const [chatAILeaseSettings, chatAILeaseRenewalSettings, chatAIMaintenanceSettings]: [
        ChatAILeaseSettingsCompanyPortalTypedSettingsWithOverridesDocument,
        ChatAILeaseRenewalSettingsCompanyPortalTypedSettingsWithOverridesDocument,
        ChatAIMaintenanceRequestsSettingsCompanyPortalTypedSettingsWithOverridesDocument,
      ] = await Promise.all([
        new chatsSettingsRepository().getAllChatAILeaseSettings(),
        new AiLeaseRenewalSettingsRepository().getAllAiLeaseRenewalSettings(),
        new chatsSettingsRepository().getAllChatAIMaintenanceRequestsSettings(),
      ]);

      return {
        chatAILease: chatAILeaseSettings?.settings,
        chatAILeaseRenewal: chatAILeaseRenewalSettings?.settings,
        chatAIMaintenance: chatAIMaintenanceSettings?.settings,
      };
    } catch (error) {
      console.error('Failed to get AI communications settings:', error);
      return null;
    }
  };

  const saveAICommunicationsSettings = createMutation(async (enabled: boolean) => {
    const existingSettings = await getAICommunicationsSettings();

    if (!existingSettings) {
      throw new Error('Failed to get existing AI settings from backend');
    }

    await Promise.all([
      existingSettings.chatAILease &&
        new chatsSettingsRepository().changeChatAILeaseSettings({
          ...existingSettings.chatAILease,
          enabled,
        }),
      existingSettings.chatAILeaseRenewal &&
        new AiLeaseRenewalSettingsRepository().updateAiLeaseRenewalSettings({
          ...existingSettings.chatAILeaseRenewal,
          enabled,
        }),
      existingSettings.chatAIMaintenance &&
        new chatsSettingsRepository().changeChatAIMaintenanceRequestsSettings({
          ...existingSettings.chatAIMaintenance,
          enabled,
        }),
    ]);

    toast.success(t('AI communications settings saved successfully'));
  });

  return {
    companies: currentCompany,
    companyCanBeCharged,
    onboardingExperience,
    isOnboardingCompleted,
    isExperienceOnboardingActive,
    shouldHideOnboardingMenu,
    updateOnboardingStep,
    completeOnboarding,
    hideOnboarding,
    needsBankAccountSetup,
    purchasePhoneNumber,
    checkPhoneNumberStatus,
    getTextMessageAlertSettings,
    saveTextMessageAlertSettings,
    getAICommunicationsSettings,
    saveAICommunicationsSettings,
  };
});
